import { isEqual } from 'lodash';
import { useQueryClient } from 'react-query';
import { isNullOrUndefined } from '@vy/utils';
import { useContract } from '../../../../hooks/requests/use-contract';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { parsePlanInput } from '../../../../utils/parse-plan-input';
import { runDebouncedMutation } from '../../../../utils/vy-mutation';
import { userMessage } from '../../../../utils/vy-toasts';
import { PlanInputForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { useReviseContract } from '../../../../hooks/requests/use-revise-contract/use-revise-contract';
import { getCreatePlanDefaultValues } from '../../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import {
  invalidateContractsQueries,
  invalidateCustomersQueries,
  invalidateBillingRevenueQueries,
} from '../../../../utils/query-invalidation';
import { queryKeys } from '../../../../services/query-key/query-keys';

export const useReviseForRenewalSubmitter = (contractId: string) => {
  const navigate = useVyNavigate();
  const queryClient = useQueryClient();

  const {
    data: { contract },
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const reviseForRenewalMutation = useReviseContract();

  return async (planInput: PlanInputForm) => {
    const parsedPlan = parsePlanInput(planInput, {
      errorContext: 'Revise contract for renewal validation',
    });

    const startDate = planInput.startDate;
    if (!parsedPlan.success || isNullOrUndefined(startDate)) {
      return;
    }

    const originalPlanData = getCreatePlanDefaultValues({
      plan: contract.plan,
      startDate: contract.startDate,
      customerId: contract.customer.id,
    });
    const hasChanges = !isEqual(originalPlanData, parsedPlan);

    if (!hasChanges) {
      userMessage('error', 'No changes detected — revision not saved.');
      return;
    }

    await runDebouncedMutation(
      `revise-contract-for-renewal-${contractId}`,
      reviseForRenewalMutation,
      {
        contractId,
        reviseContractRequest: {
          startDate,
          plan: parsedPlan.data,
        },
      },
      {
        pending: 'Updating...',
        success: 'Updated',
        error: 'Failed to revise contract',
      },
      {
        onSuccess: async () => {
          navigate(`/plans/contracts/${contractId}`);

          await Promise.all([
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey.includes(contractId),
            }),
            invalidateContractsQueries(queryClient),
            invalidateCustomersQueries(queryClient),
            invalidateBillingRevenueQueries(queryClient),
            queryClient.invalidateQueries(queryKeys.creditPools()),
          ]);
        },
      },
    );
  };
};
